import { useCallback, useEffect, useState } from 'react';
import { useDebounced } from '@client/new/hooks/useDebounced';
import { CommunityId } from '../model/community.types';
import { FeedAuthorFilter, MIN_SEARCH_LENGTH } from '../model/feedFilter.types';
import { analytics } from './analytics';

const FEED_DEBOUNCE_MS = 300;
const TRACKING_SETTLE_MS = 1000;

type UseCommunityFilters = {
  filter: FeedAuthorFilter;
  changeFilter: (next: FeedAuthorFilter) => void;
  search: string;
  setSearch: (value: string) => void;
  debouncedSearch: string;
};

export const useCommunityFilters = (
  activeId: CommunityId | null,
): UseCommunityFilters => {
  const [filter, setFilter] = useState<FeedAuthorFilter>('all');
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounced(search, FEED_DEBOUNCE_MS);
  const settledSearch = useDebounced(search, TRACKING_SETTLE_MS);

  useEffect(() => {
    const term = settledSearch.trim();
    if (term.length < MIN_SEARCH_LENGTH || activeId === null) return;
    analytics.trackSearchApplied(activeId, term);
  }, [settledSearch, activeId]);

  const changeFilter = useCallback(
    (next: FeedAuthorFilter) => {
      if (next !== filter && activeId !== null) {
        analytics.trackFilterApplied(activeId, next);
      }
      setFilter(next);
    },
    [filter, activeId],
  );

  return { filter, changeFilter, search, setSearch, debouncedSearch };
};
